import React, { useEffect, useState } from 'react';
import { fetchWithAuth } from '../utils/authUtils';
import { useElderlyFeatureAccess } from '../hooks/useFeatureAccess';
import { FEATURES, FEATURE_LABELS } from '../constants/featureAccess';
import { isMembershipDeniedError } from '../utils/membershipAccess';
import AiDisclaimerBanner from './Legal/AiDisclaimerBanner';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const PREMIUM_NOTICE_STYLE = { color: '#64748b', lineHeight: 1.6, margin: 0 };

function isPremiumFeatureNotice(message) {
    if (!message) return false;
    if (isMembershipDeniedError({ message, code: 'premium_required' })) return true;
    const text = String(message).toLowerCase();
    return text.includes('nâng cao')
    || text.includes('nâng cấp gói')
    || text.includes('premium_required');
}

// Component to display AI suggested actions (Vận động & Sinh hoạt)
export const AiActionsWidget = ({ elderlyId, showTitle = true }) => {
    const [aiData, setAiData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { canUse, loading: accessLoading } = useElderlyFeatureAccess(elderlyId);
    const canUseAi = canUse(FEATURES.AI_ENGINE);
    const premiumNotice = `Tính năng "${FEATURE_LABELS[FEATURES.AI_ENGINE]}" thuộc gói Pro (119.000đ/tháng).`;

    useEffect(() => {
        setAiData(null);
        setError('');
        setLoading(false);
    }, [elderlyId]);

    useEffect(() => {
        if (!elderlyId || accessLoading || !canUseAi) return;

        const loadAiEngine = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await fetchWithAuth(`${API_BASE}/api/ai/engine?elderlyId=${elderlyId}`);
                const payload = await response.json().catch(() => ({}));

                if (!response.ok) {
                    throw new Error(payload?.message || 'Không thể tải dữ liệu AI');
                }

                setAiData(payload?.data || null);
            } catch (err) {
                setError(err?.message || 'Lỗi khi tải dữ liệu');
                setAiData(null);
            } finally {
                setLoading(false);
            }
        };

        loadAiEngine();
    }, [elderlyId, accessLoading, canUseAi]);

    if (!accessLoading && !canUseAi) {
        return (
            <div className="ai-actions-widget ai-actions-widget--notice">
                <p style={PREMIUM_NOTICE_STYLE}>{premiumNotice}</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="ai-actions-widget" style={{ opacity: 0.6 }}>
                <p>Đang tải lời khuyên...</p>
            </div>
        );
    }

    if (error) {
        const notice = isPremiumFeatureNotice(error);
        return (
            <div className={`ai-actions-widget ${notice ? 'ai-actions-widget--notice' : ''}`} style={notice ? undefined : { opacity: 0.6 }}>
                <p style={notice ? PREMIUM_NOTICE_STYLE : { color: '#dc2626', margin: 0 }}>{error}</p>
            </div>
        );
    }

    if (!aiData?.suggested_action) {
        return (
            <div className="ai-actions-widget" style={{ opacity: 0.6 }}>
                <p>Chưa có lời khuyên. Cập nhật dữ liệu sức khỏe để nhận gợi ý từ AI.</p>
            </div>
        );
    }

    // Parse suggested_action text (simple line-based parsing)
    const actionText = String(aiData.suggested_action).trim();
    const lines = actionText.split(/\n/).filter(Boolean);

    return (
        <div className="ai-actions-widget">
            {showTitle && <h3 className="ai-actions-title">💡 Lời khuyên tuần này từ Trợ lý AI</h3>}
            <AiDisclaimerBanner />
            <div className="ai-actions-content">
                {lines.length > 0 ? (
                    lines.map((line, idx) => (
                        <p key={idx} className="ai-action-item">
                            {line}
                        </p>
                    ))
                ) : (
                    <p>{actionText}</p>
                )}
            </div>
        </div>
    );
};

// Component to display AI suggested menu (Gợi ý thực đơn)
export const AiMenuWidget = ({ elderlyId, showTitle = true }) => {
    const [aiData, setAiData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { canUse, loading: accessLoading } = useElderlyFeatureAccess(elderlyId);
    const canUseNutrition = canUse(FEATURES.AI_NUTRITION);
    const premiumNotice = `Tính năng "${FEATURE_LABELS[FEATURES.AI_NUTRITION]}" thuộc gói Pro (119.000đ/tháng).`;

    useEffect(() => {
        setAiData(null);
        setError('');
        setLoading(false);
    }, [elderlyId]);

    useEffect(() => {
        if (!elderlyId || accessLoading || !canUseNutrition) return;

        const loadAiEngine = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await fetchWithAuth(`${API_BASE}/api/ai/engine?elderlyId=${elderlyId}`);
                const payload = await response.json().catch(() => ({}));

                if (!response.ok) {
                    throw new Error(payload?.message || 'Không thể tải dữ liệu AI');
                }

                setAiData(payload?.data || null);
            } catch (err) {
                setError(err?.message || 'Lỗi khi tải dữ liệu');
                setAiData(null);
            } finally {
                setLoading(false);
            }
        };

        loadAiEngine();
    }, [elderlyId, accessLoading, canUseNutrition]);

    if (!accessLoading && !canUseNutrition) {
        return (
            <div className="ai-menu-widget ai-menu-widget--notice">
                {showTitle && <h3>🍽️ Gợi ý thực đơn</h3>}
                <p style={PREMIUM_NOTICE_STYLE}>{premiumNotice}</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="ai-menu-widget">
                {showTitle && <h3>🍽️ Gợi ý thực đơn</h3>}
                <p style={{ opacity: 0.6 }}>Đang tải thực đơn...</p>
            </div>
        );
    }

    if (error) {
        const notice = isPremiumFeatureNotice(error);
        return (
            <div className={`ai-menu-widget ${notice ? 'ai-menu-widget--notice' : ''}`}>
                {showTitle && <h3>🍽️ Gợi ý thực đơn</h3>}
                <p style={notice ? PREMIUM_NOTICE_STYLE : { color: '#dc2626', opacity: 0.6, margin: 0 }}>{error}</p>
            </div>
        );
    }

    if (!aiData?.suggested_menu) {
        return (
            <div className="ai-menu-widget">
                {showTitle && <h3>🍽️ Gợi ý thực đơn</h3>}
                <p style={{ opacity: 0.6 }}>Chưa có gợi ý thực đơn. Cập nhật dữ liệu sức khỏe để nhận gợi ý từ AI.</p>
            </div>
        );
    }

    const menuText = String(aiData.suggested_menu).trim();

    return (
        <div className="ai-menu-widget">
            {showTitle && <h3>🍽️ Gợi ý thực đơn</h3>}
            <AiDisclaimerBanner />
            <div className="ai-menu-content">
                <p style={{ whiteSpace: 'pre-line', lineHeight: '1.8' }}>{menuText}</p>
            </div>
        </div>
    );
};

export default { AiActionsWidget, AiMenuWidget };
